/**
 * hooks/usePins/index.ts
 * desc: the pages the user pinned to the sidebar — read from and written to the user's backend preferences,
 *       where userDto.preferences.pinnedUrls is a JSON string holding `{ label, url }` entries
 * note: the users endpoint replaces the whole DTO, so a write sends the current DTO with the one preference changed
 *       (see useColumnState for the same flow); the store takes the new pins on success, so the UI follows the
 *       server rather than an optimistic copy, and no write starts while one is in flight (isSaving) — a second
 *       one would be built from a list the server has not confirmed and could undo the first. The backend stores
 *       a preference as one scalar of at most 2048 characters — MAX_PINS keeps the JSON well inside it
 */

import { useDispatch, useSelector } from 'react-redux';
import { showErrorMessage } from '../../utilities';
import { updateUserMe } from '../../store/actions/users';
import type { RootState } from '../../store';

export interface Pin {
    label: string;
    url: string;
}

export const PINS_PREFERENCE_KEY = 'pinnedUrls';
export const MAX_PINS = 10;

const _isPin = (value: unknown): value is Pin =>
    !!value && typeof value === 'object'
    && typeof (value as { label?: unknown }).label === 'string'
    && typeof (value as { url?: unknown }).url === 'string';

// absent, corrupt or foreign content yields no pins; a list longer than the cap is cut, not rejected
export const parsePins = (value: unknown): Pin[] => {
    if (typeof value !== 'string') return [];
    try {
        const parsed: unknown = JSON.parse(value);
        return Array.isArray(parsed) ? parsed.filter(_isPin).slice(0, MAX_PINS) : [];
    } catch {
        return [];
    }
};

const usePins = () => {
    const dispatch = useDispatch() as unknown as (action: unknown) => Promise<unknown>;
    const userDto = useSelector((state: RootState) => state.users?.me?.userDto);
    // any profile write, not only a pin: the grid's column state travels the same way
    const isSaving = useSelector((state: RootState) => !!state.users?.isLoading?.updateUserMe);
    const pins = parsePins(userDto?.preferences?.[PINS_PREFERENCE_KEY]);

    const _save = (next: Pin[]): void => {
        if (!userDto?.id || isSaving) return;
        const payload = { ...userDto, preferences: { ...userDto.preferences, [PINS_PREFERENCE_KEY]: JSON.stringify(next) } };
        dispatch(updateUserMe({ userId: userDto.id }, payload)).catch(showErrorMessage);
    };

    const isPinned = (url: string): boolean => pins.some((pin) => pin.url === url);

    const pin = (next: Pin): void => {
        if (pins.length >= MAX_PINS || isPinned(next.url)) return;
        _save([ ...pins, next ]);
    };

    const unpin = (url: string): void => {
        if (!isPinned(url)) return;
        _save(pins.filter((item) => item.url !== url));
    };

    return { pins, canPin: pins.length < MAX_PINS, isPinned, isSaving, pin, unpin };
};

export default usePins;
